import React, { useState } from 'react';
import { GameShell, randomItem, shuffle, usePausableTimeout } from '../shared';
import brainGameLevels from '../../../lib/brainGameLevels';
import { formatGameCopy } from './gameCopy';

const PERCENTAGES = [25, 50, 75];
const TOTALS = [12, 16, 20, 24];
const { normalizeBrainGameLevel } = brainGameLevels;

const createPercentRound = (level = 3, roundIndex = 1) => {
    const percentages = level === 1 ? [50] : PERCENTAGES;
    const totals = level === 1 ? TOTALS.slice(0, 2) : level === 2 ? TOTALS.slice(0, 3) : TOTALS;
    const percentage = randomItem(percentages);
    const total = randomItem(totals);
    const answer = total * percentage / 100;
    const values = new Set([answer]);

    while (values.size < 4) {
        values.add(Math.max(1, answer + Math.floor(Math.random() * 9) - 4));
    }

    return {
        answer,
        id: `${roundIndex}-${level}-${Date.now()}`,
        options: shuffle([...values]),
        percentage,
        total,
    };
};

function PercentPopGame({ copy = {}, initialLevel, isPaused = false, ...props }) {
    const level = normalizeBrainGameLevel('percent-pop', initialLevel);
    const { schedule: scheduleTimer } = usePausableTimeout(isPaused);
    const [roundIndex, setRoundIndex] = useState(1);
    const [round, setRound] = useState(() => createPercentRound(level, 1));
    const [choice, setChoice] = useState(null);
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const correct = choice !== null && choice === round.answer;

    const choose = (value) => {
        if (isPaused || choice !== null) {
            return;
        }

        const isCorrect = value === round.answer;
        const nextStreak = isCorrect ? streak + 1 : 0;
        setChoice(value);
        setScore((current) => current + (isCorrect ? 1 : 0));
        setStreak(nextStreak);
        scheduleTimer(() => {
            const nextIndex = roundIndex + 1;
            setRoundIndex(nextIndex);
            setRound(createPercentRound(level, nextIndex));
            setChoice(null);
        }, isCorrect ? 700 : 950);
    };

    return (
        <GameShell {...props} copy={copy} disabled={isPaused} metrics={{ level, score, streak }} tone={correct ? 'good' : choice !== null ? 'miss' : 'ready'}>
            <div className="UFOV-brain-games__percent-game">
                <strong aria-label={formatGameCopy(copy.percentValue, { value: round.percentage })}>{round.percentage}%</strong>
                <div
                    aria-label={formatGameCopy(copy.dotCount, { count: round.total })}
                    className="UFOV-brain-games__percent-dots"
                    style={{ '--dot-columns': round.total % 6 === 0 ? 6 : 4 }}
                >
                    {Array.from({ length: round.total }, (_, index) => <i key={`${round.id}-${index}`} />)}
                </div>
                <div className="UFOV-brain-games__game-cue">{copy.percentPopCue}</div>
                <div className="UFOV-brain-games__quality-options">
                    {round.options.map((option) => (
                        <button
                            className={`${choice === option ? 'is-picked' : ''} ${choice !== null && option === round.answer ? 'is-correct' : ''}`.trim()}
                            disabled={isPaused || choice !== null}
                            key={`${round.id}-${option}`}
                            onClick={() => choose(option)}
                            type="button"
                        >
                            {option}
                        </button>
                    ))}
                </div>
            </div>
        </GameShell>
    );
}

export default PercentPopGame;
